/* eslint-disable no-nested-ternary */
import { useProjectParams } from '@hooks/useProjectParams';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { FilesSelector } from 'components/FilesSelector/FilesSelector';
import { ControlledSelectField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { UploadStatus } from 'components/UploadStatus';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { WorkOrderStatusEnum, WorkOrderVorTypeEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { getContractConstructionTypesAndSeasonsGql } from 'pages/Project/UerProject/Contract/hooks/useContractData';
import { Button, cnMixSpace, Grid, GridItem, IconWarning, Informer, Loader } from 'uikit';

import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { useUploadWorkOrder } from './useUploadWorkOrder';

import Styles from './WorkOrderUploadModal.module.css';

const LOAD_TITLE = 'Загрузка ведомости объемов работ';
const ADD_TITLE = 'Добавление ведомости объемов работ';
const REPLACE_TITLE = 'Замена файла ВОР';
const INFORMER = 'Загружаемый файл ВОР в формате xml будет относиться к выбранному виду строительства и сезону';

interface IWorkOrderUploadModal {
    workOrderId?: number;
    onClose?: () => void;
    onSuccess?: () => void;
    vorType?: WorkOrderVorTypeEnum | null;
    isReplace?: boolean;
}

export const WorkOrderUploadModal: React.FC<IWorkOrderUploadModal> = (props) => {
    const { workOrderId, onClose: onCloseProp, onSuccess: onSuccessProp, vorType, isReplace } = props;
    const { id: projectId, methodology } = useProjectParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const { projectData } = useProjectData(projectId, false);
    const [format, setFormat] = useState(vorType || WorkOrderVorTypeEnum.XmlBundle);
    const [files, setFiles] = useState<File[]>([]);
    const { control, reset } = useForm<any>();
    const constructionType = useWatch({ control, name: 'constructionType' });
    const season = useWatch({ control, name: 'season' });
    const isMultiple = format === WorkOrderVorTypeEnum.XmlBundle;
    const title = isReplace ? REPLACE_TITLE : workOrderId ? ADD_TITLE : LOAD_TITLE;
    const uploadTitle = isReplace ? 'Заменить' : workOrderId ? 'Добавить' : 'Создать';
    const hideInformer = !isReplace && workOrderId;

    const {
        isSubmitted,
        uploadStatementData,
        uploadStatementStatus,
        uploadStatementError,
        makeRequestHandler,
        handleCloseModalError,
        handleCreateReport,
        makeUploadWorkOrderReset,
    } = useUploadWorkOrder({
        projectId,
        contractId: projectData?.contractId,
        format,
        constructionTypeId: constructionType?.id,
        contractSeasonId: season?.id,
        workOrderId,
        isReplace,
    });
    const isUploading = uploadStatementStatus === RequestStatus.FETCHING;
    const isConfirmDisabled = !files.length || isUploading || (isMultiple && (!constructionType || !season));

    const getContractVariables = useMemo(
        () => ({
            contractId: projectData?.contractId,
        }),
        [projectData],
    );

    const [getContractConstructionTypesAndSeasonsData] = useRequest({
        requestId: 'getContractConstructionTypesAndSeasons',
        query: getContractConstructionTypesAndSeasonsGql,
        withEffect: Boolean(projectData?.contractId),
        variables: getContractVariables,
    });

    const constructionTypes = useMemo(
        () =>
            getContractConstructionTypesAndSeasonsData?.uerContract?.data?.constructionTypes?.map((t: any) => ({
                label: t.name,
                id: t.id,
            })) || [],
        [getContractConstructionTypesAndSeasonsData],
    );

    const seasons = useMemo(
        () =>
            getContractConstructionTypesAndSeasonsData?.uerContract?.data?.seasons?.map((t: any) => ({
                label: t.year,
                id: t.id,
            })) || [],
        [getContractConstructionTypesAndSeasonsData],
    );

    const typeName = useMemo(() => (format === WorkOrderVorTypeEnum.XmlBundle ? '.xml' : '.rivx'), [format]);
    const accept = useMemo(() => [typeName], [typeName]);

    const analyticsData = useMemo(
        () => ({
            projectId,
            contractId: projectData?.contractId,
            orderId: uploadStatementData?.data?.id ?? workOrderId,
            orderStatus: WorkOrderStatusEnum.OnFormation,
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        }),
        [
            projectData?.contractId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            uploadStatementData?.data?.id,
            workOrderId,
        ],
    );

    const onSuccess = useCallback(() => {
        makeUploadWorkOrderReset(undefined);

        // TODO: временно, до перехода на новое отображение
        if (pathname?.split('/')?.[6] === 'old') {
            navigate(
                `/projects/${methodology}/${projectId}/order/${workOrderId || uploadStatementData?.data?.id}/old`,
                {
                    state: { refreshTree: true, openEditWorkOrder: !workOrderId, reload: Math.random() },
                },
            );
        } else {
            navigate(`/projects/${methodology}/${projectId}/order/${workOrderId || uploadStatementData?.data?.id}`, {
                state: { refreshTree: true, openEditWorkOrder: !workOrderId, reload: Math.random() },
            });
        }

        onSuccessProp?.();

        sendGpnAnalyticsEvent({
            componentId: 'rim_get to order_pressed',
            ...analyticsData,
        });
    }, [
        analyticsData,
        makeUploadWorkOrderReset,
        methodology,
        navigate,
        onSuccessProp,
        pathname,
        projectId,
        sendGpnAnalyticsEvent,
        uploadStatementData?.data?.id,
        workOrderId,
    ]);

    const onClose = useCallback(() => {
        makeUploadWorkOrderReset(undefined);
        if (workOrderId) {
            onCloseProp?.();
        } else {
            navigate(`/projects/${methodology}/${projectId}/contract`);
        }
    }, [makeUploadWorkOrderReset, methodology, navigate, onCloseProp, projectId, workOrderId]);

    const onConfirm = useCallback(() => {
        makeRequestHandler(files);
    }, [files, makeRequestHandler]);

    const onFilesChange = useCallback((file) => {
        setFiles(file);
    }, []);

    useEffect(() => {
        setFiles([]);
        reset();
    }, [format, reset]);

    useEffect(() => {
        if (uploadStatementStatus === RequestStatus.SUCCESS) {
            if (uploadStatementData?.result === true) {
                sendGpnAnalyticsEvent({
                    componentId: 'rim_order file_saved',
                    constaeventtype: 'undefined',
                    component: 'OTHER',
                    ...analyticsData,
                });
                sendGpnAnalyticsEvent({
                    componentId: 'rim_order file success uploading_notification',
                    constaeventtype: 'change',
                    component: 'ALERT',
                    ...analyticsData,
                });
            } else {
                sendGpnAnalyticsEvent({
                    componentId: 'rim_order file error uploading_notification',
                    constaeventtype: 'change',
                    component: 'ALERT',
                    ...analyticsData,
                });
            }
        }
    }, [analyticsData, sendGpnAnalyticsEvent, uploadStatementData?.result, uploadStatementStatus]);

    const createReportHandlerWithSPAEvent = useCallback(() => {
        handleCreateReport();

        sendGpnAnalyticsEvent({
            componentId: 'rim_order file error upload_report created',
            ...analyticsData,
        });
    }, [analyticsData, handleCreateReport, sendGpnAnalyticsEvent]);

    return (
        <ModalDialog
            title={title}
            isOpen
            onConfirm={onConfirm}
            onClose={onClose}
            btnConfirmLabel={uploadTitle}
            btnCancelLabel="Отменить"
            withTitleCloseButton
            style={{ width: 600 }}
            btnConfirmDisabled={isConfirmDisabled}
        >
            {isUploading && (
                <div style={{ height: 300, position: 'relative' }}>
                    <Loader size="m" style={{ position: 'absolute', top: '50%' }} />
                </div>
            )}
            {!isUploading && (
                <div className={Styles.workOrderUploadModal}>
                    {!vorType && (
                        <div className="header">
                            <h3>Формат файла ВОР</h3>
                            <div className="tabs">
                                <Button
                                    label="XML"
                                    className={format !== WorkOrderVorTypeEnum.XmlBundle ? 'notSelected' : undefined}
                                    view="secondary"
                                    size="s"
                                    onClick={() => setFormat(WorkOrderVorTypeEnum.XmlBundle)}
                                />
                                <Button
                                    label="RIVX"
                                    className={format !== WorkOrderVorTypeEnum.Rivx ? 'notSelected' : undefined}
                                    view="secondary"
                                    size="s"
                                    onClick={() => setFormat(WorkOrderVorTypeEnum.Rivx)}
                                />
                            </div>
                        </div>
                    )}
                    <div className="body">
                        {!hideInformer && (
                            <div
                                className={`${!vorType ? 'topMessages' : 'topMessagesNoTabs'} ${
                                    isMultiple ? 'marginInformer' : ''
                                }`}
                            >
                                {isMultiple && (
                                    <Informer label={INFORMER} view="filled" status="warning" icon={IconWarning} />
                                )}
                            </div>
                        )}
                        {isMultiple && (
                            <form className={cnMixSpace({ mB: 'm' })}>
                                <Grid cols={1}>
                                    <GridItem>
                                        <ControlledSelectField
                                            control={control}
                                            label="Вид строительства"
                                            name="constructionType"
                                            items={constructionTypes}
                                            className={cnMixSpace({ mB: 's' })}
                                            rules={{ required: true }}
                                        />
                                    </GridItem>
                                </Grid>
                                <GridItem>
                                    <ControlledSelectField
                                        control={control}
                                        label="Сезон"
                                        name="season"
                                        items={seasons}
                                        rules={{ required: true }}
                                    />
                                </GridItem>
                            </form>
                        )}
                        <FilesSelector
                            accept={accept}
                            isLoading={isUploading}
                            typeName={typeName}
                            manualOperationName="выберите файл вручную"
                            onFilesChange={onFilesChange}
                            multiple={isMultiple}
                            heightContent
                        />
                        {uploadStatementStatus === RequestStatus.SUCCESS && uploadStatementData?.result === true && (
                            <UploadStatus
                                isOpened
                                handleCloseModal={onClose}
                                statusText="Проверка на соответствие ВОР прейскурантам завершена. Ошибок не обнаружено"
                                titleText={LOAD_TITLE}
                                status="success"
                                withTitleCloseButton={false}
                            >
                                <Button label="Перейти в наряд-заказ" onClick={onSuccess} />
                            </UploadStatus>
                        )}
                        <ErrorHandlingModal
                            titleText={LOAD_TITLE}
                            isSubmitted={isSubmitted}
                            statusRequest={uploadStatementStatus}
                            dataRequest={uploadStatementData}
                            errorsRequest={uploadStatementError}
                            handleCloseModalError={handleCloseModalError}
                            handleCreateReport={createReportHandlerWithSPAEvent}
                        />
                    </div>
                </div>
            )}
        </ModalDialog>
    );
};

WorkOrderUploadModal.displayName = 'WorkOrderUploadModal';
